import React, {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {ServiceData} from '../../redux/services/ServiceRedux';
import {useSmartCaptcha} from '../SmartCaptcha/useSmartCaptcha';
import SmartCaptchaBox from '../SmartCaptcha/SmartCaptchaBox';
import ModalFormFeedback from '../ModalFormFeedback/ModalFormFeedback';
import {SelectValuesFormSimple} from '../../services/content/Constants';
import {content} from '../../services/content';
import {IContactForms} from '../../redux/initState/InterfacesState';

interface Props {
  contactForms?: IContactForms;
  contactFormId?: number;
  /** Событие заявки от блока: detail — поля формы (name, phone, email, newsletter). */
  submitEvent?: string;
  /** Событие «открыть стандартную модалку обратной связи». */
  modalEvent?: string;
  eventMetrika?: string;
  modalTitle?: string;
}

export interface MkFormBridgeResult {
  ok: boolean;
  message: string;
}

/**
 * Мост между формами блоков главной (mk_letsgo, новая шапка mk-header) и общим
 * путём отправки заявок SmartCaptcha → CF7 → amoCRM.
 *
 * Формы в этих блоках — обычная вёрстка со своими скриптами (анимация машинки,
 * панели мобильного меню), переписывать их на React незачем. Скрипт блока сам
 * валидирует поля и диспатчит на document отменяемое событие submitEvent;
 * мост вызывает preventDefault() — блок понимает, что заявку взяли, — проходит
 * капчу, отправляет форму и отвечает событием `${submitEvent}:result`
 * с detail {ok, message}.
 *
 * modalEvent открывает модалку обратной связи — так кнопка «Подключить
 * доставку» новой шапки работает на десктопе.
 */
const MkFormBridge = ({contactForms, contactFormId, submitEvent, modalEvent, eventMetrika, modalTitle}: Props) => {
  const captcha = useSmartCaptcha(contactForms);
  const captchaRef = useRef(captcha);
  captchaRef.current = captcha;
  const busyRef = useRef(false);
  const [showFeedback, setShowFeedback] = useState(false);

  useEffect(() => {
    if (!submitEvent) {
      return;
    }

    const respond = (result: MkFormBridgeResult) => {
      busyRef.current = false;
      document.dispatchEvent(new CustomEvent(`${submitEvent}:result`, {detail: result}));
    };

    const onSubmit = async (e: Event) => {
      e.preventDefault();
      if (busyRef.current) {
        return;
      }
      busyRef.current = true;

      const detail = ((e as CustomEvent).detail || {}) as Record<string, any>;
      const formId = contactFormId || contactForms?.main_contact_form_id;
      if (!formId) {
        respond({ok: false, message: 'Заявка не отправлена по техническим причинам. Позвоните нам, пожалуйста.'});
        return;
      }

      let token = '';
      try {
        token = await captchaRef.current.ensureToken();
      } catch (err) {
        console.error('[form-bridge] капча не выдала токен', {reason: (err as any)?.message ?? String(err)});
        respond({ok: false, message: 'Не удалось пройти проверку, попробуйте отправить ещё раз'});
        return;
      }

      ServiceData.submitForm({
        data: {
          // В CF7-форме имя (username) обязательное, а в шапке поле «Имя» по макету
          // необязательное — без подстановки заявка падала бы на валидации.
          name: detail.name || 'Не указано',
          phone: detail.phone || '',
          email: detail.email || '',
          allow_marketing: detail.newsletter ? '1' : '',
        },
        token,
        eventMetrika: eventMetrika || '',
        contactFormId: formId,
        success: () => {
          captchaRef.current.reset();
          respond({ok: true, message: 'Спасибо! Мы получили вашу заявку и скоро свяжемся с вами.'});
        },
        setServerText: (text: string) => {
          captchaRef.current.reset();
          respond({ok: false, message: text || 'Произошла ошибка при отправке формы'});
        },
      } as any);
    };

    document.addEventListener(submitEvent, onSubmit);
    return () => document.removeEventListener(submitEvent, onSubmit);
  }, [submitEvent, contactFormId, contactForms, eventMetrika]);

  useEffect(() => {
    if (!modalEvent) {
      return;
    }
    const onOpen = (e: Event) => {
      e.preventDefault();
      setShowFeedback(true);
    };
    document.addEventListener(modalEvent, onOpen);
    return () => document.removeEventListener(modalEvent, onOpen);
  }, [modalEvent]);

  return (
    <>
      {submitEvent ? <SmartCaptchaBox captcha={captcha}/> : null}
      {modalEvent && contactForms ? createPortal(
        <ModalFormFeedback
          hasCssTransition
          showForm={showFeedback}
          id="feedback"
          title={modalTitle || content.header.textModalFeedback}
          select={{options: SelectValuesFormSimple, active: SelectValuesFormSimple[1]}}
          closedModal={() => setShowFeedback(false)}
          contactForms={contactForms}
        />,
        document.body
      ) : null}
    </>
  );
};

export default MkFormBridge;
